import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, KeyRound, Building2 } from "lucide-react";
import { NATIONALITES, TITRE_SEJOUR_TYPES, isForeign } from "@/lib/etatCivil";

const contrats = ["CDI", "CDD", "Intérim", "Saisonnier", "Apprentissage", "Stage"];
const CIVIL = { date_naissance: "", nationalite: "", sexe: "", titre_sejour_type: "", titre_sejour_numero: "" };

export default function ChefSalaries() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState(null); // null = multi-sites désactivé
  const [siteFilter, setSiteFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [createdInfo, setCreatedInfo] = useState(null);
  const [pwdSalarie, setPwdSalarie] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [pwdResult, setPwdResult] = useState(null);
  const [params, setParams] = useSearchParams();

  function empty() {
    return { nom: "", prenom: "", poste: "", contrat: "CDI", email: "", telephone: "", password: "", conges_payes_balance: 25, taux_horaire: 12, site_id: "", ...CIVIL };
  }

  const load = async () => {
    try {
      const { data } = await api.get("/chef/salaries");
      setItems(data);
      const code = params.get("edit");
      if (code) { const s = data.find((x) => x.code === code); if (s) openEdit(s); setParams({}, { replace: true }); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
    api.get("/chef/sites").then((r) => setSites(r.data || [])).catch(() => setSites(null));
  }, []);

  const siteName = (id) => sites?.find((s) => s.id === id)?.name;
  const visible = siteFilter === "all" ? items : siteFilter === "none" ? items.filter((s) => !s.site_id) : items.filter((s) => s.site_id === siteFilter);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, site_id: form.site_id || (editing ? "" : null) };
      if (editing) {
        await api.patch(`/chef/salaries/${editing.id}`, payload);
        toast.success("Salarié mis à jour");
        setOpen(false);
        setEditing(null);
      } else {
        const { data } = await api.post("/chef/salaries", payload);
        setCreatedInfo(data);
      }
      setForm(empty());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(empty());
    setCreatedInfo(null);
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      nom: s.nom, prenom: s.prenom, poste: s.poste, contrat: s.contrat,
      email: s.email || "", telephone: s.telephone || "", password: "", conges_payes_balance: s.conges_payes_balance || 25,
      taux_horaire: s.taux_horaire ?? 12, status: s.status, site_id: s.site_id || "",
      date_naissance: s.date_naissance || "", nationalite: s.nationalite || "", sexe: s.sexe || "", titre_sejour_type: s.titre_sejour_type || "", titre_sejour_numero: s.titre_sejour_numero || "",
    });
    setCreatedInfo(null);
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce salarié ?")) return;
    try {
      await api.delete(`/chef/salaries/${id}`);
      toast.success("Salarié supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openPwdReset = (s) => {
    setPwdSalarie(s);
    setNewPwd("");
    setPwdResult(null);
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) { toast.error("Minimum 6 caractères"); return; }
    try {
      const { data } = await api.post(`/chef/salaries/${pwdSalarie.id}/password`, { password: newPwd });
      setPwdResult(data);
      toast.success("Mot de passe modifié");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Équipe"
        title="Gestion des salariés"
        sub="Créez, modifiez et suspendez vos salariés. Chaque salarié reçoit un code d'accès unique."
        right={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setCreatedInfo(null); } }}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="chef-add-salarie-btn" className="h-11 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
                <Plus className="h-4 w-4 mr-2" /> Ajouter un salarié
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-black tracking-tight">
                  {editing ? "Modifier le salarié" : "Nouveau salarié"}
                </DialogTitle>
              </DialogHeader>
              {createdInfo ? (
                <div className="space-y-4">
                  <div className="p-4 bg-[#ECFDF5] border border-[#A7F3D0] text-sm">Salarié créé avec succès.</div>
                  <div className="p-4 border border-[#DEE2E6] bg-[#F8F9FA] font-mono text-sm space-y-2" data-testid="salarie-created-info">
                    <div><span className="text-[#868E96]">Code :</span> <span className="font-bold">{createdInfo.code}</span></div>
                    <div><span className="text-[#868E96]">Mot de passe :</span> <span className="font-bold">{createdInfo.password}</span></div>
                  </div>
                  <Button onClick={() => { setOpen(false); setCreatedInfo(null); }} className="w-full bg-[#0A0A0A] text-white">Terminer</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="chef-salarie-form">
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Nom" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} testid="input-nom" required />
                    <F label="Prénom" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} testid="input-prenom" required />
                  </div>
                  <F label="Poste" value={form.poste} onChange={(v) => setForm({ ...form, poste: v })} testid="input-poste" required />
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Contrat</Label>
                    <Select value={form.contrat} onValueChange={(v) => setForm({ ...form, contrat: v })}>
                      <SelectTrigger data-testid="select-contrat" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {contrats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <F label="Email (optionnel)" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="input-email" />
                  {sites !== null && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Établissement</Label>
                      <Select value={form.site_id || "none"} onValueChange={(v) => setForm({ ...form, site_id: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="select-site" className="h-11"><SelectValue placeholder="— Non affecté —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Non affecté —</SelectItem>
                          {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_siege ? " (siège)" : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <F label="Téléphone *" type="tel" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} testid="input-telephone" required helper="Format FR: 06xxxxxxxx ou +33..." />
                  <div className="border border-[#DEE2E6] bg-[#F8F9FA] p-3 space-y-3" data-testid="salarie-etat-civil">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96]">État civil — registre du personnel (facultatif)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <F label="Date de naissance" type="date" value={form.date_naissance} onChange={(v) => setForm({ ...form, date_naissance: v })} testid="input-date-naissance" />
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Sexe</Label>
                        <Select value={form.sexe || "none"} onValueChange={(v) => setForm({ ...form, sexe: v === "none" ? "" : v })}>
                          <SelectTrigger data-testid="select-sexe" className="h-11"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">— Non renseigné —</SelectItem><SelectItem value="F">Féminin</SelectItem><SelectItem value="M">Masculin</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Nationalité</Label>
                      <Input list="nationalites" value={form.nationalite} onChange={(e) => setForm({ ...form, nationalite: e.target.value })} data-testid="input-nationalite" className="h-11 border-[#DEE2E6]" placeholder="Française" />
                      <datalist id="nationalites">{NATIONALITES.map((n) => <option key={n} value={n} />)}</datalist>
                    </div>
                    {isForeign(form.nationalite) && (
                      <div className="grid grid-cols-2 gap-3" data-testid="salarie-titre-sejour">
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Titre de séjour</Label>
                          <Select value={form.titre_sejour_type || "none"} onValueChange={(v) => setForm({ ...form, titre_sejour_type: v === "none" ? "" : v })}>
                            <SelectTrigger data-testid="select-titre-type" className="h-11"><SelectValue placeholder="Type" /></SelectTrigger>
                            <SelectContent><SelectItem value="none">— Type —</SelectItem>{TITRE_SEJOUR_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <F label="N° du titre" value={form.titre_sejour_numero} onChange={(v) => setForm({ ...form, titre_sejour_numero: v })} testid="input-titre-numero" />
                        <div className="col-span-2 text-[10px] text-[#868E96]">Mention obligatoire au registre du personnel pour les travailleurs hors UE / EEE / Suisse.</div>
                      </div>
                    )}
                  </div>
                  {!editing && (
                    <F label="Mot de passe initial" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="input-password" required />
                  )}
                  <F label="Solde congés payés (jours)" type="number" value={form.conges_payes_balance} onChange={(v) => setForm({ ...form, conges_payes_balance: parseFloat(v) })} testid="input-conges" />
                  <F label="Taux horaire (€ / heure)" type="number" value={form.taux_horaire} onChange={(v) => setForm({ ...form, taux_horaire: parseFloat(v) })} testid="input-taux-horaire" required />
                  {editing && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Statut</Label>
                      <Select value={form.status || "actif"} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger data-testid="select-status" className="h-11"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="actif">Actif</SelectItem>
                          <SelectItem value="suspendu">Suspendu</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="submit" data-testid="chef-salarie-submit" className="bg-[#002FA7] hover:bg-[#002175] text-white">
                      {editing ? "Enregistrer" : "Créer"}
                    </Button>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-6 lg:p-10">
        {sites !== null && (
          <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="salaries-site-filter-bar">
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#868E96]">Établissement</Label>
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger data-testid="salaries-site-filter" className="h-10 w-64 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les établissements</SelectItem>
                <SelectItem value="none">Non affectés</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-[#868E96]" data-testid="salaries-site-count">{visible.length} salarié{visible.length > 1 ? "s" : ""}</span>
          </div>
        )}
        <div className="bg-white border border-[#DEE2E6]" data-testid="chef-salaries-list">
          <div className="grid grid-cols-12 gap-4 p-4 text-xs uppercase tracking-[0.2em] font-semibold bg-[#F8F9FA] border-b border-[#DEE2E6]">
            <div className="col-span-3">Nom</div>
            <div className="col-span-2">Poste</div>
            <div className="col-span-1">Contrat</div>
            <div className="col-span-2">{sites !== null ? "Établissement" : "Code"}</div>
            <div className="col-span-1">Taux €/h</div>
            <div className="col-span-1">Statut</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {visible.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#868E96]">Aucun salarié.</div>
          ) : visible.map((s) => (
            <div key={s.id} className="grid grid-cols-12 gap-4 p-4 border-b border-[#DEE2E6] items-center text-sm" data-testid={`salarie-row-${s.code}`}>
              <div className="col-span-3 font-semibold">{s.prenom} {s.nom}{sites !== null && <div className="font-mono text-[10px] text-[#868E96] font-normal">{s.code}</div>}</div>
              <div className="col-span-2">{s.poste}</div>
              <div className="col-span-1">{s.contrat}</div>
              <div className="col-span-2 text-xs" data-testid={`salarie-site-${s.code}`}>
                {sites !== null
                  ? (siteName(s.site_id) ? <span className="inline-flex items-center gap-1 font-semibold"><Building2 className="h-3 w-3 text-[#002FA7]" /> {siteName(s.site_id)}</span> : <span className="text-[#868E96] italic">Non affecté</span>)
                  : <span className="font-mono">{s.code}</span>}
              </div>
              <div className="col-span-1 font-mono text-xs font-bold" data-testid={`salarie-taux-${s.code}`}>{(s.taux_horaire ?? 12).toFixed(2)} €</div>
              <div className="col-span-1">
                <span className={`status-pill ${s.status === "actif" ? "status-active" : "status-inactive"}`}>
                  {s.status === "actif" ? "Actif" : "Suspendu"}
                </span>
              </div>
              <div className="col-span-2 text-right flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`salarie-edit-${s.code}`}><Edit3 className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => openPwdReset(s)} data-testid={`salarie-pwd-${s.code}`} className="border-[#FFD700] text-[#0A0A0A] hover:bg-[#FFD700]"><KeyRound className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(s.id)} data-testid={`salarie-delete-${s.code}`} className="border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Password reset dialog */}
      <Dialog open={!!pwdSalarie} onOpenChange={(o) => { if (!o) { setPwdSalarie(null); setPwdResult(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[#FFD700]" /> Modifier le mot de passe
            </DialogTitle>
          </DialogHeader>
          {pwdSalarie && !pwdResult && (
            <form onSubmit={submitPwd} className="space-y-4" data-testid="pwd-reset-form">
              <div className="p-3 bg-[#F8F9FA] border border-[#DEE2E6] text-sm">
                <div className="text-xs uppercase tracking-[0.2em] text-[#868E96] font-semibold">Salarié</div>
                <div className="font-bold mt-1">{pwdSalarie.prenom} {pwdSalarie.nom}</div>
                <div className="font-mono text-xs text-[#495057]">{pwdSalarie.code}</div>
              </div>
              <F label="Nouveau mot de passe (min. 6 caractères)" type="text" value={newPwd} onChange={setNewPwd} testid="pwd-new" required />
              <DialogFooter>
                <Button type="submit" data-testid="pwd-submit" className="bg-[#002FA7] text-white">Enregistrer</Button>
              </DialogFooter>
            </form>
          )}
          {pwdResult && (
            <div className="space-y-4" data-testid="pwd-reset-result">
              <div className="p-4 bg-[#ECFDF5] border border-[#A7F3D0] text-sm font-semibold">Mot de passe mis à jour avec succès.</div>
              <div className="p-4 border border-[#DEE2E6] bg-[#F8F9FA] font-mono text-sm space-y-2">
                <div><span className="text-[#868E96]">Code :</span> <span className="font-bold">{pwdResult.code}</span></div>
                <div><span className="text-[#868E96]">Mot de passe :</span> <span className="font-bold">{pwdResult.password}</span></div>
              </div>
              <Button onClick={() => { setPwdSalarie(null); setPwdResult(null); }} className="w-full bg-[#0A0A0A] text-white">Fermer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid, required, helper }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">{label}</Label>
      <Input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 border-[#DEE2E6] focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
        required={required}
      />
      {helper && <div className="text-[10px] text-[#868E96]">{helper}</div>}
    </div>
  );
}
